import React, { useState } from "react"
import { Input, Button, Empty, Card, Typography, Dropdown, Flex } from "antd"
import { SearchOutlined, PlusOutlined, EllipsisOutlined } from "@ant-design/icons"
import styles from "./CustomTemplateHeader.module.css"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import TemplateEmptyImage from "ui/Icon/DSIcon/TemplateEmptyImage"
import { Text } from "ui/Typography"
import { useTranslation } from "react-i18next"
import CustomPagination from "components/common/CustomPagination"
import Spinner from "components/common/Spinner"
import { useTemplates } from "./hooks/useTemplates"
import DataTable from "ui/DataTable"
import { useNavigate } from "react-router-dom"
import { routes } from "router"
import { useDispatch, useSelector } from "react-redux"
import { deleteQuestionnaireTemplate, duplicateQuestionnaireTemplate, getQuestionnaireTemplates } from "store/question"
import DeleteConfirmation from "./modal/DeleteConfirmation"
import { toaster } from "components/common/Toast"
import TripSelector from "components/TripSelector"
import { Search } from "ui/Icon/DSIcon/Search"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import { useDevice } from "hooks/useDevice"
import DataTableResponsive from "ui/DataTableResponsive"
import useActiveGuide from "hooks/useActiveClient"

export const CustomTemplate = () => {
  const [searchText, setSearchText] = useState("")
  const [deleteModal, setDeleteModal] = useState({ visible: false, template: null })
  const [openDropdownId, setOpenDropdownId] = useState(null)

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { isMobile } = useDevice()
  const { isActive: active, isPending } = useActiveGuide()
  const { loading: isLoadingDeleteTemplate } = useSelector((state) => state.question.deleteTemplate)
  const { loading: isLoadingUpdateExperience, templateId: updatingTemplateId } = useSelector((state) => state.question.experienceUpdate)
  // Use custom hook for templates data and pagination
  const { templates, loading, metadata, pagination, handlePageChange, handlePageSizeChange } = useTemplates(searchText)

  const getTemplateDetailsRoute = (templateId) => routes.proSettingsEditTemplateDetails({ id: templateId })

  const handleAddTemplate = () => {
    if (active && !isPending) {
      navigate(routes.proSettingsTemplateDetails())
    }
  }

  const handleEditTemplate = (template) => {
    navigate(getTemplateDetailsRoute(template.uuid))
  }

  const handleDuplicateTemplate = async (template) => {
    try {
      await dispatch(duplicateQuestionnaireTemplate(template.uuid))
      toaster.success("Template duplicated successfully")
      dispatch(getQuestionnaireTemplates(1, 10))
    } catch (error) {
      toaster.error(error?.message || "Failed to duplicate template")
    }
  }

  const handleDeleteClick = (template) => {
    setDeleteModal({ visible: true, template })
  }

  const closeDeleteModal = () => {
    setDeleteModal({ visible: false, template: null })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteModal.template) return

    try {
      const result = await dispatch(deleteQuestionnaireTemplate(deleteModal.template.uuid))
      if (result?.message) {
        toaster.success(result.message)
        // Refresh the templates list by triggering a page change
        handlePageChange(1)
      }
    } catch (error) {
      toaster.error(error.message || "Failed to delete template")
    } finally {
      closeDeleteModal()
    }
  }

  const handleDeleteCancel = () => {
    closeDeleteModal()
  }

  const handleTemplateNavigate = (event, uuid) => {
    event.stopPropagation()
    navigate(getTemplateDetailsRoute(uuid))
  }

  const getTemplateCellProps = (record, style = {}) => ({
    role: "button",
    tabIndex: 0,
    onClick: (event) => {
      handleTemplateNavigate(event, record.uuid)
    },
    onKeyDown: (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        handleTemplateNavigate(event, record.uuid)
      }
    },
    onTouchStart: (event) => {
      event.stopPropagation()
    },
    style: { cursor: "pointer", ...style }
  })

  const getActionMenuItems = (record) => [
    {
      key: "edit",
      label: t("settings.edit_action"),
      onClick: () => handleEditTemplate(record)
    },
    {
      key: "duplicate",
      label: t("settings.duplicate_action"),
      onClick: () => handleDuplicateTemplate(record)
    },
    {
      key: "delete",
      label: t("settings.delete_action"),
      danger: true,
      onClick: () => handleDeleteClick(record)
    }
  ]

  const columns = [
    {
      title: t("settings.template_name"),
      dataIndex: "name",
      key: "name",
      onCell: (record) => getTemplateCellProps(record),
      render: (text) => <Typography.Text strong>{text}</Typography.Text>
    },
    {
      title: t("settings.template_description"),
      dataIndex: "description",
      key: "description",
      onCell: (record) => getTemplateCellProps(record, { maxWidth: 300 }),
      render: (text) => (
        <Typography.Text ellipsis={{ tooltip: text }} style={{ maxWidth: 300 }}>
          {text}
        </Typography.Text>
      )
    },
    {
      title: t("settings.experiences"),
      dataIndex: "trips",
      key: "trips",
      width: 300,
      render: (trips, record) => {
        if (!record) return null
        const isUpdatingThisRow = isLoadingUpdateExperience && updatingTemplateId === record.uuid
        return (
          <div className={styles.trips}>
            {isUpdatingThisRow ? (
              <Spinner size="small" />
            ) : (
              <TripSelector
                uuid={record.uuid}
                trips={trips}
                allTrips={record.experience_list}
                isDisabled={!!openDropdownId && openDropdownId !== record.uuid}
                isOpen={openDropdownId === record.uuid}
                onOpenChange={(isOpen) => setOpenDropdownId(isOpen ? record.uuid : null)}
                onSelect={(selectedIds) => {
                  console.log(selectedIds)
                }}
                isLoading={isUpdatingThisRow}
              />
            )}
          </div>
        )
      }
    },
    {
      title: "Actions",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Dropdown menu={{ items: getActionMenuItems(record) }} trigger={["click"]} placement="bottomRight">
          <Button type="text" aria-label="Template actions" onClick={(event) => event.stopPropagation()}>
            <EllipsisOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
          </Button>
        </Dropdown>
      )
    }
  ]

  const handleRowClick = (record) => {
    console.log(record, "record")
  }

  const tableData = templates.map((template) => ({
    ...template,
    key: template.id
  }))

  const mobileTableData = tableData.map((template) => {
    const isUpdatingThisRow = isLoadingUpdateExperience && updatingTemplateId === template.uuid

    return {
      key: template.key,
      uuid: template.uuid,
      name: template.name,
      description: template.description,
      trips: template.trips,
      experience_list: template.experience_list,
      experiences: (
        <div
          className={styles.trips}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {isUpdatingThisRow ? (
            <Spinner size="small" />
          ) : (
            <TripSelector
              uuid={template.uuid}
              trips={template.trips}
              allTrips={template.experience_list}
              isDisabled={!!openDropdownId && openDropdownId !== template.uuid}
              isOpen={openDropdownId === template.uuid}
              onOpenChange={(isOpen) => setOpenDropdownId(isOpen ? template.uuid : null)}
              onSelect={(selectedIds) => {
                console.log(selectedIds)
              }}
              isLoading={isUpdatingThisRow}
            />
          )}
        </div>
      )
    }
  })

  const mobileLabelMap = {
    name: t("settings.template_name"),
    description: t("settings.template_description"),
    experiences: t("settings.experiences")
  }

  const mobileFieldOrder = ["name", "description", "experiences"]

  const getMobileTableActions = (record) => [
    {
      label: t("settings.edit_action"),
      onClick: () => handleEditTemplate(record),
      color: "primary",
      variant: "outlined"
    },
    {
      label: t("settings.duplicate_action"),
      onClick: () => handleDuplicateTemplate(record),
      color: "primary",
      variant: "outlined"
    },
    {
      label: t("settings.delete_action"),
      onClick: () => handleDeleteClick(record),
      color: "danger",
      variant: "outlined"
    }
  ]

  return (
    <Flex vertical style={{ width: "100%" }}>
      <div className={styles.headerBar}>
        <Input
          className={styles.searchInput}
          placeholder="Search"
          prefix={<SearchOutlined style={{ color: "#bdbdbd" }} />}
          allowClear
          onChange={(e) => setSearchText(e.target.value)}
          value={searchText}
        />
        <Button
          color="primary"
          size="large"
          variant="solid"
          className={styles.addButton}
          icon={<PlusOutlined aria-hidden="true" />}
          onClick={handleAddTemplate}
          disabled={!active || isPending}
          aria-disabled={!active || isPending}
        >
          {t("settings.add_template")}
        </Button>
      </div>

      <FirstLoading name="question.questionnaireTemplates">
        <WithEmptyStateWrapper
          fallback={
            loading ? (
              <Spinner />
            ) : (
              <Card className={styles.contentWrapper}>
                <Empty
                  image={searchText ? <Search /> : <TemplateEmptyImage />}
                  description={
                    <Flex vertical gap={12} align="center" direction="column">
                      <Text strong size="lg">
                        {searchText ? t("clients.no_exact_match") : t("settings.not_created_any_template")}
                      </Text>
                      {!searchText && <Typography.Text type="secondary">{t("settings.your_template_displayed_here")}</Typography.Text>}
                      {!searchText && (
                        <Button color="primary" variant="solid" onClick={handleAddTemplate} disabled={!active || isPending}>
                          Add Template
                        </Button>
                      )}
                    </Flex>
                  }
                />
              </Card>
            )
          }
          name="question.questionnaireTemplates"
          hasItems={templates.length > 0}
          tag="div"
        >
          {templates.length > 0 && (
            <Flex vertical gap={24} style={{ marginTop: 24 }}>
              {isMobile ? (
                <DataTableResponsive
                  data={mobileTableData}
                  labelMap={mobileLabelMap}
                  fieldOrder={mobileFieldOrder}
                  onItemClick={(record) => navigate(getTemplateDetailsRoute(record.uuid))}
                  actionIcon={<EllipsisOutlined />}
                  getTableActions={getMobileTableActions}
                />
              ) : (
                <div className={styles.tableWrapper}>
                  <DataTable columns={columns} dataSource={tableData} pagination={false} onRowClick={handleRowClick} rowClickKey="uuid" />
                </div>
              )}

              <Flex justify={isMobile ? "center" : "flex-end"} className={styles.paginationRow}>
                <CustomPagination
                  metadata={{
                    ...metadata,
                    page: metadata.page || 1,
                    total_pages: metadata.total_pages || 1,
                    next_page: metadata.next_page || null,
                    prev_page: metadata.prev_page || null
                  }}
                  pageSize={pagination.pageSize}
                  total={metadata.total_results || 0}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                />
              </Flex>
            </Flex>
          )}
        </WithEmptyStateWrapper>

        <DeleteConfirmation
          visible={deleteModal.visible}
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          loading={isLoadingDeleteTemplate}
          title={`${t("settings.delete_template_default_title")} ?`}
          content={
            deleteModal.template?.name ? (
              <>
                <div>
                  {t("settings.delete_confirmation_template")}
                  <strong>{deleteModal.template?.name}?</strong>
                </div>
                <div>{t("settings.delete_confirmation_desc")}</div>
              </>
            ) : null
          }
        />
      </FirstLoading>
    </Flex>
  )
}
